// apps/frontend/src/services/websocketService.ts
// ⚡ Service Socket.io Premium - Compatible avec votre backend enterprise

// socket.io-client (~124 KB) est importé dynamiquement dans connect() : la
// lib ne charge plus au démarrage de l'app, mais quand le temps réel
// s'active (après l'auth). Le type Socket reste en import type (effacé au
// build, coût zéro).
import type { Socket } from 'socket.io-client';
import type { WSEvent, ChatMessage } from '../types/chat';
import { API_BASE_URL } from '../config/api';

export interface NotificationPayload {
  id: string;
  type: string;
  title: string;
  message: string;
  actionUrl?: string;
  actionLabel?: string;
  priority: string;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
  imageUrl?: string;
  data?: Record<string, unknown>;
}

interface WSOptions {
  url?: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

type EventHandler = (event: WSEvent) => void;
type ConnectionHandler = (connected: boolean) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private url: string;
  private eventHandlers: Set<EventHandler> = new Set();
  private connectionHandlers: Set<ConnectionHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private isConnecting = false;

  constructor(options: WSOptions = {}) {
    this.url = options.url || API_BASE_URL;
    this.maxReconnectAttempts = options.maxReconnectAttempts || 10;
  }

  async connect(accessToken: string): Promise<void> {
    const { io } = await import('socket.io-client');
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        reject(new Error('Connection already in progress'));
        return;
      }

      this.isConnecting = true;

      this.socket = io(this.url, {
        auth: {
          token: accessToken,
        },
        transports: ['websocket', 'polling'],
        upgrade: true,
        rememberUpgrade: true,
        timeout: 10000,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      });

      this.socket.on('connect', () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.notifyConnectionHandlers(true);
        resolve();
      });

      this.socket.on('disconnect', _reason => {
        this.isConnecting = false;
        this.notifyConnectionHandlers(false);
      });

      this.socket.on('connect_error', error => {
        console.error('❌ Socket.io connection error:', error);
        this.isConnecting = false;
        this.reconnectAttempts++;

        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          reject(
            new Error(
              `Failed to connect after ${this.maxReconnectAttempts} attempts`
            )
          );
        } else {
          reject(error);
        }
      });

      this.socket.on('reconnect', () => {
        this.reconnectAttempts = 0;
      });

      this.socket.on('reconnect_error', error => {
        console.error('🔄 Socket.io reconnection error:', error);
      });

      // Écouter les événements chat spécifiques
      this.setupChatEventListeners();
    });
  }

  private setupChatEventListeners() {
    if (!this.socket) return;

    // Messages reçus - COMPATIBLE AVEC VOS TYPES WSMessageEvent
    this.socket.on('messageReceived', data => {
      this.notifyEventHandlers({
        type: 'message',
        conversationId: data.conversationId,
        message: data as ChatMessage,
      });
    });

    // Indicateurs de frappe - COMPATIBLE AVEC VOS TYPES WSTypingEvent
    this.socket.on('typingStarted', data => {
      this.notifyEventHandlers({
        type: 'typing',
        conversationId: data.conversationId,
        userId: data.userId,
        isTyping: true,
      });
    });

    this.socket.on('typingStopped', data => {
      this.notifyEventHandlers({
        type: 'typing',
        conversationId: data.conversationId,
        userId: data.userId,
        isTyping: false,
      });
    });

    // Status utilisateur - COMPATIBLE AVEC VOS TYPES WSUserStatusEvent
    this.socket.on('userStatusChanged', data => {
      this.notifyEventHandlers({
        type: 'user_status',
        userId: data.userId,
        isOnline: data.isOnline,
        lastSeenAt: data.lastSeenAt,
      });
    });

    // Autres événements qui ne sont PAS dans vos types WSEvent
    // On les gère séparément pour éviter les erreurs TypeScript
    this.socket.on('conversationUpdated', data => {
      // Événement personnalisé - pas dans WSEvent
      this.eventHandlers.forEach(handler => {
        try {
          // Cast en any pour les événements non typés
          (handler as any)({ type: 'conversation_updated', data });
        } catch (error) {
          console.error('❌ Event handler error:', error);
        }
      });
    });

    this.socket.on('messageTranslated', data => {
      this.eventHandlers.forEach(handler => {
        try {
          (handler as any)({ type: 'translation', data });
        } catch (error) {
          console.error('❌ Event handler error:', error);
        }
      });
    });

    this.socket.on('offerReceived', data => {
      this.eventHandlers.forEach(handler => {
        try {
          (handler as any)({ type: 'offer', data });
        } catch (error) {
          console.error('❌ Event handler error:', error);
        }
      });
    });

    this.socket.on('conversationRead', data => {
      this.eventHandlers.forEach(handler => {
        try {
          (handler as any)({ type: 'read', data });
        } catch (error) {
          console.error('❌ Event handler error:', error);
        }
      });
    });

    // Gestion des erreurs
    this.socket.on('error', error => {
      console.error('❌ Socket.io server error:', error);
      this.eventHandlers.forEach(handler => {
        try {
          (handler as any)({ type: 'error', data: error });
        } catch (error) {
          console.error('❌ Event handler error:', error);
        }
      });
    });
  }

  // ── Notifications ──────────────────────────────────────────────────────────

  subscribeToNotifications() {
    this.socket?.emit('subscribeToNotifications');
  }

  fetchNotifications(opts?: {
    limit?: number;
    offset?: number;
    unreadOnly?: boolean;
  }) {
    this.socket?.emit('fetchNotifications', opts ?? {});
  }

  markNotificationRead(notificationId: string) {
    this.socket?.emit('markNotificationRead', notificationId);
  }

  markAllNotificationsRead() {
    this.socket?.emit('markAllNotificationsRead');
  }

  onNewNotification(handler: (notification: NotificationPayload) => void) {
    this.socket?.on('newNotification', handler);
    return () => this.socket?.off('newNotification', handler);
  }

  onNotificationsReceived(
    handler: (data: {
      notifications: NotificationPayload[];
      total: number;
      hasMore: boolean;
    }) => void
  ) {
    this.socket?.on('notificationsReceived', handler);
    return () => this.socket?.off('notificationsReceived', handler);
  }

  onNotificationReadConfirmed(
    handler: (data: { notificationId: string; readAt: string }) => void
  ) {
    this.socket?.on('notificationReadConfirmed', handler);
    return () => this.socket?.off('notificationReadConfirmed', handler);
  }

  onAllNotificationsReadConfirmed(handler: (data: { count: number }) => void) {
    this.socket?.on('allNotificationsReadConfirmed', handler);
    return () => this.socket?.off('allNotificationsReadConfirmed', handler);
  }

  // Actions chat
  sendTyping(conversationId: string, isTyping: boolean) {
    if (this.socket?.connected) {
      const event = isTyping ? 'startTyping' : 'stopTyping';
      // Backend expects a raw string, not an object
      this.socket.emit(event, conversationId);
    }
  }

  joinConversation(conversationId: string) {
    if (this.socket?.connected) {
      this.socket.emit('joinConversation', conversationId);
    }
  }

  leaveConversation(conversationId: string) {
    if (this.socket?.connected) {
      this.socket.emit('leaveConversation', conversationId);
    }
  }

  markAsRead(conversationId: string) {
    if (this.socket?.connected) {
      this.socket.emit('markAsRead', { conversationId });
    }
  }

  // Gestionnaires d'événements
  onEvent(handler: EventHandler) {
    this.eventHandlers.add(handler);
    return () => this.eventHandlers.delete(handler);
  }

  onConnection(handler: ConnectionHandler) {
    this.connectionHandlers.add(handler);
    return () => this.connectionHandlers.delete(handler);
  }

  private notifyEventHandlers(event: WSEvent) {
    this.eventHandlers.forEach(handler => {
      try {
        handler(event);
      } catch (error) {
        console.error('❌ Event handler error:', error);
      }
    });
  }

  private notifyConnectionHandlers(connected: boolean) {
    this.connectionHandlers.forEach(handler => {
      try {
        handler(connected);
      } catch (error) {
        console.error('❌ Connection handler error:', error);
      }
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
    }
  }

  // Getters
  get isConnected(): boolean {
    return this.socket?.connected || false;
  }

  get connectionState():
    | 'connecting'
    | 'connected'
    | 'disconnected'
    | 'reconnecting' {
    if (!this.socket) return 'disconnected';
    if (this.isConnecting) return 'connecting';
    if (this.socket.connected) return 'connected';
    if (this.socket.disconnected && this.reconnectAttempts > 0)
      return 'reconnecting';
    return 'disconnected';
  }

  get socketId(): string | undefined {
    return this.socket?.id;
  }
}

// Instance singleton
export const wsService = new WebSocketService();
export default wsService;
